import { useAppDispatch } from 'app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditNestedProductMutation, useGetNestedProductQuery } from '../services';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { trimValues } from 'utils/object-helpers';
import { useRole } from 'hooks/useRole';
import { useWorkspace } from 'hooks/useWorkspace';
import { useGetPlacesQuery } from 'features/place/services';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useTranslation } from 'react-i18next';
import { useGetNestedProductCategoriesQuery } from 'features/nested-product-category/services';
import { NESTED_PRODUCT_FIELDS_SCHEMA, NESTED_PRODUCT_INITIAL_FIELDS } from '../constants/nested-product-fields';
import { handleConfigFieldChange } from 'utils/products-config-price-helper';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { NestedProduct } from '../types/dtos';
import { PlaceConfigData } from 'types/dtos';
import { NESTED_PRODUCT_STATUS } from '../constants/status';

type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

const useController = () => {
    const { nestedProductId, placeId = '' } = useParams();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [editNestedProduct, { isLoading: isEditNestedProductLoading }] = useEditNestedProductMutation();
    const { data: nestedProduct } = useGetNestedProductQuery(nestedProductId as string);
    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        website_id: websiteId,
    });
    const { data: nestedProductCategories, isLoading: isGetNestedProductCategoriesLoading } =
        useGetNestedProductCategoriesQuery(FETCH_ALL_PARAMS);
    const { formData, setInitialFormData, handleChange, handleSubmit, errors, isDirty, changedValues } = useForm({
        validationSchema: NESTED_PRODUCT_FIELDS_SCHEMA,
        options: {
            trackChangedValues: true,
        },
    });
    const [placeConfigData, setPlaceConfigData] = useState<PlaceConfigData[]>([]);
    const [updateAll, setUpdateAll] = useState<boolean>(false);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const { isAdmin } = useRole();
    const allErrors: AllErrorsType<typeof NESTED_PRODUCT_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);
    const initialPlaceConfigsRef = useRef<PlaceConfigData[]>([]);
    useEffect(() => {
        const place = places?.content.find((place) => place.id === placeId);
        const placeConfig = nestedProduct?.placeNestedProductConfig?.find(
            (placeConfig) => placeConfig.placeId === placeId,
        );

        if (nestedProduct && place && placeConfig) {
            const newPlaceConfigs = [
                {
                    id: placeConfig?.id,
                    placeId: place.id,
                    placeName: place?.name,
                    price: placeConfig.price ? placeConfig.price.toString() : '',
                    discountedPrice: placeConfig?.discountedPrice ? placeConfig.discountedPrice.toString() : '',
                    isAvailable: placeConfig?.isAvailable
                        ? `${NESTED_PRODUCT_STATUS.AVAILABLE}`
                        : `${NESTED_PRODUCT_STATUS.AVAILABLE}`,
                },
            ];

            setPlaceConfigData(newPlaceConfigs);

            if (initialPlaceConfigsRef.current.length === 0) {
                initialPlaceConfigsRef.current = [...newPlaceConfigs];
            }

            delete nestedProduct.placeConfigs;

            setInitialFormData({ ...NESTED_PRODUCT_INITIAL_FIELDS, ...nestedProduct });
        }
    }, [nestedProduct, setInitialFormData, placeId, places?.content]);

    const hasPlaceConfigsChanged = useMemo(() => {
        if (initialPlaceConfigsRef.current.length !== placeConfigData.length) {
            return true;
        }
        if (updateAll) {
            return true;
        }

        return !initialPlaceConfigsRef.current.every((initialConfig, index) => {
            const currentConfig = placeConfigData[index];
            return (
                initialConfig.price === currentConfig.price &&
                initialConfig.discountedPrice === currentConfig.discountedPrice &&
                initialConfig.isAvailable === currentConfig.isAvailable &&
                initialConfig.placeId === currentConfig.placeId
            );
        });
    }, [placeConfigData, updateAll]);

    const validatePrices = useCallback(
        (configs: PlaceConfigData[]): Record<string, ValidationErrorType> => {
            return validateConfigFields<PlaceConfigData>(configs, 'placeId', ['price', 'discountedPrice'], {
                price: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_POSITIVE'),
                    },
                },
                discountedPrice: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: [
                        {
                            validator: (value, allValues) =>
                                !allValues.price.trim() || Number(value) < Number(allValues.price),
                            message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                        },
                    ],
                },
            });
        },
        [t],
    );

    const handlePlaceConfigPriceChange = useCallback(
        (field: string, value: string, placeId?: string) => {
            handleConfigFieldChange({
                field,
                value,
                id: placeId,
                currentData: placeConfigData,
                setData: setPlaceConfigData,
                errorState: priceErrors,
                setErrorState: setPriceErrors,
                validateFn: validatePrices,
                errorPrefixes: ['price-', 'discountedPrice-'],
            });
        },
        [placeConfigData, priceErrors, setPlaceConfigData, setPriceErrors, validatePrices],
    );

    const handleSucces = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'nestedProduct.NESTED_PRODUCT_EDITED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...changedValues };

        let configsToUse = placeConfigData;
        if (updateAll) {
            const baseConfig = placeConfigData[0];
            const updatedConfigs =
                places?.content
                    ?.filter((place) => place.id !== baseConfig?.placeId)
                    .map((place) => ({
                        id: nestedProduct?.placeNestedProductConfig?.find(
                            (placeConfig) => placeConfig.placeId === place.id,
                        )?.id,
                        placeId: place.id,
                        placeName: place.name,
                        price: baseConfig?.price ? baseConfig?.price : '',
                        discountedPrice: baseConfig?.discountedPrice,
                        isAvailable: baseConfig?.isAvailable,
                    })) || [];

            configsToUse = [...placeConfigData, ...updatedConfigs];
        }

        const placeConfigsPayload = configsToUse.map((config) => {
            const { placeId, isAvailable, price, discountedPrice } = config;
            return {
                placeId,
                price: price ? Number(price) : null,
                discountedPrice: discountedPrice ? Number(discountedPrice) : null,
                isAvailable: isAvailable === `${NESTED_PRODUCT_STATUS.AVAILABLE}` ? true : false,
            };
        });
        data.placeConfigs = placeConfigsPayload;

        return trimValues(data);
    }, [changedValues, placeConfigData, updateAll, places?.content, nestedProduct?.placeNestedProductConfig]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await editNestedProduct({ id: nestedProductId as string, payload }).unwrap();

                    handleSucces();

                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [editNestedProduct, getPayload, handleErrors, handleSubmit, handleSucces, nestedProductId, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData: formData as NestedProduct,
        places,
        nestedProductCategories,
        onChange: handleChange,
        onSubmit,
        onCancel,
        dataChanged: isDirty || hasPlaceConfigsChanged,
        isLoading: formData === undefined || isGetPlacesLoading || isGetNestedProductCategoriesLoading,
        isEditProductLoading: isEditNestedProductLoading,
        errors: allErrors,
        isAdmin,
        placeConfigData,
        isEdit: true,
        updateAll,
        setUpdateAll,
        onConfigPriceChange: handlePlaceConfigPriceChange,
        placeId,
    };
};

export default useController;
